import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

export type AgentAvailability = 'available' | 'pause' | 'lunch' | 'busy' | 'offline';

export const AGENT_STATUS_LABELS: Record<AgentAvailability, string> = {
  available: 'Tilgængelig',
  pause: 'Pause',
  lunch: 'Frokost',
  busy: 'Optaget',
  offline: 'Offline',
};

export const AGENT_STATUS_COLORS: Record<AgentAvailability, string> = {
  available: 'bg-emerald-500',
  pause: 'bg-amber-500',
  lunch: 'bg-orange-500',
  busy: 'bg-red-500',
  offline: 'bg-gray-400',
};

export interface AgentStatusRow {
  id: string;
  user_id: string;
  company_id: string;
  status: AgentAvailability;
  updated_at: string;
}

/** Get all agent statuses for the current company */
export function useAgentStatuses() {
  const { user } = useAuth();
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: ['agent-statuses', user?.company_id],
    queryFn: async () => {
      if (!user?.company_id) return [];
      const { data, error } = await supabase
        .from('agent_statuses')
        .select('*')
        .eq('company_id', user.company_id);
      if (error) throw error;
      return (data || []) as AgentStatusRow[];
    },
    enabled: !!user?.company_id,
  });

  // Realtime subscription
  useEffect(() => {
    if (!user?.company_id) return;

    const channel = supabase
      .channel(`agent-statuses-realtime-${user.company_id}-${Math.random().toString(36).slice(2, 8)}`)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'agent_statuses',
        filter: `company_id=eq.${user.company_id}`,
      }, () => {
        qc.invalidateQueries({ queryKey: ['agent-statuses', user.company_id] });
      })
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [user?.company_id, qc]);

  return query;
}

/** Get current user's status — trusts cron-managed statuses directly */
export function useMyAgentStatus() {
  const { user } = useAuth();
  const { data: statuses } = useAgentStatuses();
  
  const myStatus = statuses?.find(s => s.user_id === user?.id);
  return myStatus?.status ?? 'offline';
}

/** Upsert current user's agent status */
export function useSetAgentStatus() {
  const { user } = useAuth();
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (status: AgentAvailability) => {
      if (!user?.id || !user?.company_id) throw new Error('Not authenticated');

      const { error } = await supabase
        .from('agent_statuses')
        .upsert(
          {
            user_id: user.id,
            company_id: user.company_id,
            status,
            updated_at: new Date().toISOString(),
          } as any,
          { onConflict: 'user_id,company_id' }
        );
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['agent-statuses', user?.company_id] });
    },
  });
}

/** Upsert agent status for a specific user (admin use) */
export function useSetAgentStatusForUser() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async ({ userId, companyId, status }: { userId: string; companyId: string; status: AgentAvailability }) => {
      const { error } = await supabase
        .from('agent_statuses')
        .upsert(
          {
            user_id: userId,
            company_id: companyId,
            status,
            updated_at: new Date().toISOString(),
          } as any,
          { onConflict: 'user_id,company_id' }
        );
      if (error) throw error;
    },
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['agent-statuses', vars.companyId] });
    },
  });
}
